import { Button, Modal, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useAppUpdateCheck, useAppUpdatePrompt } from '@hooks/use-app-update';

// The modal can't be dismissed: the only way out is the reload.
const ignoreClose = () => undefined;

/**
 * Makes the tab reload once a build marked required is deployed — one that
 * changed the API under this tab's code. An ordinary deploy never opens it:
 * the tab moves on at the next page change instead (AppUpdateNavigationWatcher).
 *
 * Also owns the /version.json poll, because it is the one piece mounted on
 * every screen. A plain reload is enough to pick the build up: index.html is
 * served no-store and every asset name is content-hashed, so the reloaded page
 * references the new build's files.
 */
export default function AppUpdateModal() {
  useAppUpdateCheck();
  const opened = useAppUpdatePrompt();
  const { t } = useLanguage();
  const [reloading, setReloading] = useState(false);

  const reload = () => {
    setReloading(true);
    window.location.reload();
  };

  return (
    <Modal
      opened={opened}
      onClose={ignoreClose}
      centered
      size="sm"
      radius="md"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      // Above the notification stack (10001).
      zIndex={10002}
      overlayProps={{ backgroundOpacity: 0.6, blur: 4 }}
    >
      <Stack align="center" gap="sm" py="md">
        <ThemeIcon size={56} radius="xl" variant="light">
          <IconRefresh size={30} />
        </ThemeIcon>
        <Text fw={700} size="lg" ta="center">
          {t('appUpdate.title')}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {t('appUpdate.message')}
        </Text>
        <Button fullWidth mt="sm" loading={reloading} leftSection={<IconRefresh size={16} />} onClick={reload}>
          {t('appUpdate.reload')}
        </Button>
      </Stack>
    </Modal>
  );
}
